import LocalSpace, { LocalSpaceConfig } from 'localspace';
import { StorageInterface } from './storage.util';

export const Drivers = {
    IndexedDB: LocalSpace.INDEXEDDB,
    LocalStorage: LocalSpace.LOCALSTORAGE
};

type Driver = string;

export interface _LocalSpaceConfig extends LocalSpaceConfig {
    driver?: Driver[];
}

const defaultConfig: _LocalSpaceConfig = {
    name: '__storage',
    storeName: '__storageKv',
    driver: [
        Drivers.IndexedDB,
        Drivers.LocalStorage
    ]
};

export class LocalSpaceStorage implements StorageInterface<typeof LocalSpace, _LocalSpaceConfig>{
    #databaseInstance: Promise<typeof LocalSpace>;

    constructor(public readonly config: _LocalSpaceConfig) {
        this.config = { ...defaultConfig, ...config };
        this.#databaseInstance = this.create();
    }

    async create(): Promise<typeof LocalSpace> {
        const db = LocalSpace.createInstance(this.config);
        await db.setDriver(this.config.driver || []);
        return db;
    }

    async get<T>(key: string): Promise<T | undefined> {
        const db = await this.#databaseInstance;
        return db.getItem(key).then(res => typeof res === 'undefined' ? res : res as any);
    }

    async set<T>(key: string, value: T): Promise<void> {
        const db = await this.#databaseInstance;
        return db.setItem(key, value).then(() => { });
    }

    async remove(key: string): Promise<boolean> {
        const db = await this.#databaseInstance;
        return db.removeItem(key).then(() => true);
    }

    async clear(): Promise<void> {
        const db = await this.#databaseInstance;
        const keys = await this.keys();

        const p = keys.map(async key => db.removeItem(key));
        await Promise.all(p);
        return;
    }

    async length(): Promise<number> {
        const db = await this.#databaseInstance;
        return db.length();
    }

    async keys(): Promise<string[]> {
        const db = await this.#databaseInstance;
        return db.keys();
    }

    async forEach<T>(iteratorCallback: (value: T, key: string, iterationNumber: number) => any): Promise<T | undefined> {
        const db = await this.#databaseInstance;
        return db.iterate(iteratorCallback);
    }
}
